import { useState, useMemo } from "react";
import { Button, Dropdown, message } from "antd";
import {
  PrinterOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  DownOutlined,
  LoadingOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";

const BRAND = "#52BD91";

const getAlign = (col) =>
  col.align === "right" ? "right" : col.align === "center" ? "center" : "left";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const iconStyle = (color) => ({
  color,
  fontSize: 16,
  marginRight: 4,
});

const PdfExcelPrint = ({ data = [], columns = [], fileName = "export" }) => {
  const [busy, setBusy] = useState(null); // "print" | "pdf" | "excel" | null
  const [open, setOpen] = useState(false);

  /* ---------- PRINT ---------- */
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      message.warning("Pop-up blocked. Allow pop-ups to print.");
      return;
    }

    const head = columns
      .map(
        (col) =>
          `<th style="text-align:${getAlign(col)}">${escapeHtml(col.title)}</th>`,
      )
      .join("");

    const body = data
      .map(
        (item) =>
          `<tr>${columns
            .map(
              (col) =>
                `<td style="text-align:${getAlign(col)}">${escapeHtml(
                  item[col.dataIndex],
                )}</td>`,
            )
            .join("")}</tr>`,
      )
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>${escapeHtml(fileName)}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 16px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #000; padding: 8px; font-size: 12px; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  /* ---------- PDF ---------- */
  const handlePDF = () => {
    const doc = new jsPDF();

    const columnStyles = {};
    columns.forEach((col, index) => {
      columnStyles[index] = { halign: getAlign(col) };
    });

    autoTable(doc, {
      head: [columns.map((col) => col.title)],
      body: data.map((item) =>
        columns.map((col) => {
          const value = item[col.dataIndex];
          if (value === null || value === undefined) return "";
          return typeof value === "object" ? String(value) : value;
        }),
      ),
      columnStyles,
      styles: { fontSize: 6, cellPadding: 1 },
      headStyles: {
        fillColor: [211, 211, 211],
        textColor: [0, 0, 0],
        fontStyle: "normal",
        lineWidth: 0.1,
        lineColor: [211, 211, 211],
      },
      theme: "grid",
    });

    doc.save(`${fileName}.pdf`);
  };

  /* ---------- EXCEL ---------- */
  const handleExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(String(fileName).slice(0, 31));

    worksheet.columns = columns.map((col) => ({
      header: col.title,
      key: col.dataIndex,
      width: 25,
    }));

    const thin = { style: "thin" };
    const border = { top: thin, left: thin, bottom: thin, right: thin };

    data.forEach((item) => {
      const row = worksheet.addRow(item);
      columns.forEach((col, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        cell.alignment = { horizontal: getAlign(col) };
        if (typeof item[col.dataIndex] === "number") {
          cell.value = Number(item[col.dataIndex]);
        }
      });
    });

    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "D3D3D3" },
      };
    });

    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = border;
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${fileName}.xlsx`);
  };

  /* ---------- ACTION RUNNER ---------- */
  const run = async (key, action, successText) => {
    if (!data.length) {
      message.warning("There is no data to export.");
      return;
    }
    setBusy(key);
    try {
      // Let the menu close and the spinner paint before heavy work starts
      await new Promise((resolve) => setTimeout(resolve, 150));
      await action();
      if (successText) message.success(successText);
    } catch (error) {
      console.error(`Export (${key}) failed:`, error);
      message.error(`Export failed: ${error?.message || "Please try again."}`);
    } finally {
      setBusy(null);
    }
  };

  const items = useMemo(
    () => [
      {
        key: "print",
        label: "Print",
        icon: <PrinterOutlined style={iconStyle(BRAND)} />,
      },
      {
        key: "pdf",
        label: "Export as PDF",
        icon: <FilePdfOutlined style={iconStyle("#FF0955")} />,
      },
      {
        key: "excel",
        label: "Export as Excel",
        icon: <FileExcelOutlined style={iconStyle("#1D8A4E")} />,
      },
    ],
    [],
  );

  const onMenuClick = ({ key }) => {
    setOpen(false);
    if (key === "print") run("print", handlePrint);
    if (key === "pdf") run("pdf", handlePDF, "PDF downloaded");
    if (key === "excel") run("excel", handleExcel, "Excel file downloaded");
  };

  return (
    <Dropdown
      menu={{
        items,
        onClick: onMenuClick,
        style: { minWidth: 180, padding: 6 },
      }}
      trigger={["click"]}
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
      disabled={!!busy}
    >
      <Button
        type="primary"
        loading={!!busy}
        icon={busy ? <LoadingOutlined /> : <ExportOutlined />}
        style={{
          backgroundColor: BRAND,
          borderColor: BRAND,
          boxShadow: "none",
          fontWeight: 500,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        Export
        <DownOutlined
          style={{
            fontSize: 10,
            transition: "transform 0.2s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Button>
    </Dropdown>
  );
};

export default PdfExcelPrint;
